import { useTheme } from '../ThemeContext';
import ResearchBook from '../components/ResearchBook';
import { useState, useEffect, useRef } from 'react';

const SPECS = [
  { label: 'Architecture', value: 'Hybrid LSTM' },
  { label: 'LSTM Units', value: '50' },
  { label: 'Output Layer', value: 'Dense (1)' },
  { label: 'Optimizer', value: 'Adam' },
  { label: 'Loss Function', value: 'MSE' },
  { label: 'Inference Engine', value: 'ONNX Runtime' },
  { label: 'Sequence Length', value: '30 timesteps' },
  { label: 'Normalization', value: 'Min-Max Scaling' },
];

const METRICS = [
  { label: 'Accuracy', value: '94.68%', color: '#00cc66' },
  { label: 'R² Score', value: '0.9067', color: '#00cc66' },
  { label: 'MAE', value: '0.2009', color: '#0096dc' },
  { label: 'RMSE', value: '0.4414', color: '#0096dc' },
  { label: 'Precision', value: '1.0000', color: '#00cc66' },
  { label: 'Recall', value: '0.7766', color: '#f0a500' },
];

const APPROACHES = [
  { name: 'Single-Step', issue: 'Simple but only predicts one value at a time', result: 'Low utility for future planning', ok: false },
  { name: 'Pure Recursive', issue: 'Error accumulates → phase shift, large drift', result: 'Degrades over long sequences', ok: false },
  { name: 'Multi-Step', issue: 'Predicts multiple but loses real-data grounding', result: 'Moderate accuracy', ok: false },
  { name: 'Hybrid (this work)', issue: 'Predicts blocks of 10, resets with real data', result: 'Best accuracy, minimal drift ✓', ok: true },
];



const APPS = [
  {   title: 'Robotics Control', desc: 'Proactive delay compensation for 5G-connected robot actuation systems.' },
  {   title: 'Industry 4.0', desc: 'Smart factory QoS monitoring with predictive network management.' },
  {   title: 'Autonomous Vehicles', desc: 'V2X latency prediction for safety-critical driving decisions.' },
  {  title: 'Remote Surgery', desc: 'Ultra-reliable delay prediction for teleoperated medical devices.' },
  {  title: 'Telecom Operations', desc: 'SLA monitoring, congestion prediction and proactive optimization.' },
  {  title: 'Cloud Gaming', desc: 'Real-time delay forecasting for adaptive bitrate compensation.' },
];

export default function About() {
  const { dark } = useTheme();
  const [inView, setInView] = useState({});
  const refs = useRef({});

  useEffect(() => {
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => { if (e.isIntersecting) setInView(p => ({...p, [e.target.dataset.s]: true})); });
    }, { threshold: 0.12 });
    Object.values(refs.current).forEach(el => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);
  const ref = k => el => { refs.current[k] = el; };

  // theme
  const bg      = dark ? '#060c18' : '#f4f8ff';
  const border  = dark ? 'rgba(0,212,255,0.13)' : 'rgba(0,120,200,0.13)';
  const accent  = dark ? '#00d4ff' : '#0078c8';
  const text1   = dark ? '#e8f4ff' : '#0a1e3d';
  const text2   = dark ? '#8ab4d4' : '#3a5a7a';
  const text3   = dark ? '#4a7090' : '#7090b0';
  const gridC   = dark ? 'rgba(0,212,255,0.04)' : 'rgba(0,120,200,0.05)';
  const cardBg  = dark ? '#0d1929' : '#ffffff';
  const cardShad= dark ? 'none' : '0 2px 16px rgba(0,100,200,0.07)';
  const specGrid= dark ? 'rgba(0,212,255,0.1)' : 'rgba(0,120,200,0.11)';
  const specBg  = dark ? '#060c18' : '#ffffff';

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700;900&family=Inter:wght@300;400;500;600;700&display=swap');
        *{box-sizing:border-box;}
        .ar{
          background:${bg};color:${text1};
          font-family:'Inter',sans-serif;
          min-height:100vh;padding-top:62px;overflow-x:hidden;
          transition:background 0.35s,color 0.35s;
        }
        .ar::before{
          content:'';position:fixed;inset:0;
          background-image:linear-gradient(${gridC} 1px,transparent 1px),linear-gradient(90deg,${gridC} 1px,transparent 1px);
          background-size:52px 52px;pointer-events:none;z-index:0;
        }

        /* hero */
        .a-hero{
          position:relative;z-index:1;height:280px;overflow:hidden;
          display:flex;align-items:flex-end;padding:0 0 40px 40px;
        }
        .a-hero-bg{
          position:absolute;inset:0;
          background-image:url('/pic1.jpg');
          background-size:cover;background-position:center;
          filter:${dark?'brightness(0.22) saturate(1.1)':'brightness(0.2) saturate(0.8)'};
        }
        .a-hero-ov{
          position:absolute;inset:0;
          background:${dark?'linear-gradient(0deg,rgba(6,12,24,0.98) 0%,rgba(6,12,24,0.5) 60%,transparent)':'linear-gradient(0deg,rgba(244,248,255,0.98) 0%,rgba(244,248,255,0.5) 60%,transparent)'};
        }
        .a-hero-c{position:relative;z-index:1;}
        .a-eyebrow{font-size:11px;font-weight:600;letter-spacing:3px;color:${accent};text-transform:uppercase;margin-bottom:6px;}
        .a-title{font-family:'Orbitron',monospace;font-size:clamp(24px,4vw,44px);font-weight:900;color:${text1};}
        .a-sub{font-size:14px;color:${text2};margin-top:4px;}

        .ai{position:relative;z-index:1;max-width:1000px;margin:0 auto;padding:60px 24px 80px;}

        .fade-in{opacity:0;transform:translateY(24px);transition:opacity 0.6s ease,transform 0.6s ease;}
        .fade-in.vis{opacity:1;transform:none;}

        .slabel{
          font-family:'Inter',sans-serif;font-size:11px;font-weight:600;
          letter-spacing:3px;color:${text3};text-transform:uppercase;
          margin-bottom:18px;display:flex;align-items:center;gap:12px;
          transition:color 0.3s;
        }
        .slabel::after{content:'';flex:1;height:1px;background:${border};}
        .stitle{font-family:'Orbitron',monospace;font-size:clamp(20px,3vw,30px);font-weight:700;color:${text1};margin-bottom:10px;}
        .sbody{font-size:15px;color:${text2};line-height:1.8;margin-bottom:32px;max-width:680px;}

        /* model block */
        .mblock{
          border:1px solid ${border};background:${cardBg};
          padding:28px;margin-bottom:48px;border-radius:8px;
          position:relative;box-shadow:${cardShad};
          transition:background 0.3s;
        }
        .mblock::before{content:'';position:absolute;top:0;left:0;width:3px;height:100%;background:${accent};border-radius:8px 0 0 8px;}
        .specs-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:1px;background:${specGrid};border:1px solid ${border};}
        .spcel{background:${specBg};padding:14px 15px;transition:background 0.2s;}
        .spcel:hover{background:${dark?'rgba(0,212,255,0.05)':'rgba(0,120,200,0.04)'};}
        .spk{font-size:10px;font-weight:600;letter-spacing:1.5px;color:${text3};text-transform:uppercase;margin-bottom:5px;}
        .spv{font-family:'Orbitron',monospace;font-size:12px;font-weight:700;color:${accent};}

        /* metrics */
        .met-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:14px;margin-bottom:48px;}
        .met-card{
          border:1px solid ${border};background:${cardBg};padding:20px;
          border-radius:8px;text-align:center;box-shadow:${cardShad};
          transition:all 0.25s;
        }
        .met-card:hover{transform:translateY(-3px);border-color:${accent}55;}
        .met-val{font-family:'Orbitron',monospace;font-size:26px;font-weight:900;line-height:1;margin-bottom:6px;}
        .met-label{font-size:12px;font-weight:600;color:${text3};letter-spacing:1px;text-transform:uppercase;}

        /* approaches */
        .app-table{width:100%;border-collapse:collapse;margin-bottom:48px;font-size:14px;}
        .app-table th{font-size:11px;font-weight:600;letter-spacing:1.5px;color:${text3};text-align:left;padding:10px 14px;border-bottom:1px solid ${border};text-transform:uppercase;}
        .app-table td{padding:13px 14px;border-bottom:1px solid ${dark?'rgba(255,255,255,0.04)':'rgba(0,0,0,0.04)'};color:${text2};}
        .app-table tr:hover td{background:${dark?'rgba(0,212,255,0.03)':'rgba(0,120,200,0.03)'};}
        .badge-ok{display:inline-block;padding:2px 10px;border-radius:12px;font-size:11px;font-weight:600;background:${dark?'rgba(0,204,102,0.15)':'rgba(0,160,80,0.1)'};color:${dark?'#00cc66':'#007740'};border:1px solid ${dark?'rgba(0,204,102,0.3)':'rgba(0,160,80,0.2)'};}
        .badge-no{display:inline-block;padding:2px 10px;border-radius:12px;font-size:11px;font-weight:600;background:${dark?'rgba(255,80,80,0.12)':'rgba(200,30,30,0.07)'};color:${dark?'#ff7777':'#cc2020'};border:1px solid ${dark?'rgba(255,80,80,0.25)':'rgba(200,30,30,0.15)'};}

        /* dataset block */
        .ds-grid{display:grid;grid-template-columns:1fr 1fr;gap:32px;align-items:start;margin-bottom:48px;}
        .ds-img{border-radius:8px;overflow:hidden;border:1px solid ${border};box-shadow:${cardShad};}
        .ds-img img{width:100%;height:220px;object-fit:cover;display:block;filter:${dark?'brightness(0.8)':'brightness(1)'};transition:filter 0.3s;}
        .ds-info{display:flex;flex-direction:column;gap:12px;}
        .info-row{padding:13px 15px;border:1px solid ${border};border-radius:6px;background:${cardBg};box-shadow:${cardShad};transition:border-color 0.2s;}
        .info-row:hover{border-color:${accent}66;}
        .info-label{font-size:10px;font-weight:700;letter-spacing:2px;color:${accent};text-transform:uppercase;margin-bottom:4px;}
        .info-val{font-size:13px;color:${text1};font-weight:500;line-height:1.5;}
        .csv-box{background:${dark?'#000d1a':'#f6f9ff'};border:1px solid ${border};padding:16px;border-radius:6px;font-family:monospace;font-size:13px;}
        .csv-h{color:${accent};margin-bottom:6px;font-size:11px;letter-spacing:1px;font-family:'Inter',sans-serif;font-weight:600;}
        .csv-r{color:${text2};line-height:1.8;}
        .csv-r span{color:${text1};}

        /* stack + apps */
        .st-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:14px;margin-bottom:48px;}
        .st-card{border:1px solid ${border};background:${cardBg};padding:20px;border-radius:6px;box-shadow:${cardShad};transition:all 0.25s;}
        .st-card:hover{border-color:${accent}44;transform:translateY(-2px);}
        .st-cat{font-size:10px;font-weight:700;letter-spacing:2px;color:${accent};text-transform:uppercase;margin-bottom:12px;}
        .st-items{display:flex;flex-wrap:wrap;gap:7px;}
        .st-tag{font-size:12px;font-weight:500;color:${text2};border:1px solid ${border};padding:3px 10px;border-radius:3px;background:${dark?'rgba(255,255,255,0.03)':'rgba(0,0,0,0.02)'};}

        .ap-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:14px;margin-bottom:48px;}
        .ap-card{border:1px solid ${border};background:${cardBg};padding:20px;border-radius:6px;box-shadow:${cardShad};transition:all 0.25s;}
        .ap-card:hover{border-color:${accent}44;transform:translateY(-3px);}
        .ap-icon{font-size:26px;margin-bottom:10px;}
        .ap-title{font-size:14px;font-weight:700;color:${text1};margin-bottom:6px;}
        .ap-desc{font-size:13px;color:${text2};line-height:1.6;}

        @media(max-width:900px){
          .specs-grid{grid-template-columns:repeat(2,1fr);}
          .met-grid{grid-template-columns:repeat(2,1fr);}
          .ds-grid{grid-template-columns:1fr;}
          .st-grid{grid-template-columns:1fr;}
          .ap-grid{grid-template-columns:repeat(2,1fr);}
        }
      `}</style>

      <div className="ar">
        {/* hero with pic1 */}
        <div className="a-hero">
          <div className="a-hero-bg"/>
          <div className="a-hero-ov"/>
          <div className="a-hero-c">
            <div className="a-eyebrow"> System Documentation</div>
            <div className="a-title">About the Research</div>
            <div className="a-sub">KTH ExPECA · Hybrid LSTM · IP/RLC/MAC/PHY Layer Analysis</div>
          </div>
        </div>

        <div className="ai">

          {/* ── RESEARCH BOOK ── */}
          <ResearchBook dark={dark} />

          {/* ── MODEL ARCHITECTURE ── */}
          <div data-s="m1" ref={ref('m1')}>
            <div className={`fade-in${inView.m1?' vis':''}`}>
              <div className="slabel"> 01 · Model Architecture</div>
              <div className="stitle">Hybrid LSTM Design</div>
              <p className="sbody">
                The prediction engine uses a <strong>Hybrid Block-Wise Recursive LSTM</strong> — 50 LSTM units feeding a single Dense output layer. Given 30 past IP delay samples, it predicts the next <strong>10 future values recursively</strong>, then resets with real data to prevent error accumulation. This achieves an R² of 0.9067 and outperforms ARIMA, RNN, and pure recursive LSTM across all metrics.
              </p>
              <div className="mblock">
                <div className="specs-grid">
                  {SPECS.map(s=>(
                    <div className="spcel" key={s.label}>
                      <div className="spk">{s.label}</div>
                      <div className="spv">{s.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* ── METRICS ── */}
          <div data-s="m2" ref={ref('m2')}>
            <div className={`fade-in${inView.m2?' vis':''}`}>
              <div className="slabel"> 02 · Performance Metrics</div>
              <div className="stitle">Test Dataset Results</div>
              <p className="sbody">Evaluated on TE1.csv — held-out 5G packet traces not seen during training.</p>
              <div className="met-grid">
                {METRICS.map(m=>(
                  <div className="met-card" key={m.label}>
                    <div className="met-val" style={{color:m.color}}>{m.value}</div>
                    <div className="met-label">{m.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── PREDICTION APPROACHES ── */}
          <div data-s="m3" ref={ref('m3')}>
            <div className={`fade-in${inView.m3?' vis':''}`}>
              <div className="slabel"> 03 · Prediction Strategy Comparison</div>
              <div className="stitle">Why Hybrid Wins</div>
              <p className="sbody">Four approaches were evaluated. Key insight: predicting in small blocks of 10 then resetting with real data is the critical differentiator.</p>
              <table className="app-table">
                <thead>
                  <tr><th>Approach</th><th>Problem / Solution</th><th>Outcome</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {APPROACHES.map(a=>(
                    <tr key={a.name}>
                      <td style={{fontWeight:600,color:text1}}>{a.name}</td>
                      <td>{a.issue}</td>
                      <td>{a.result}</td>
                      <td><span className={a.ok?'badge-ok':'badge-no'}>{a.ok?'Best':'Rejected'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── DATASET ── */}
          <div data-s="m4" ref={ref('m4')}>
            <div className={`fade-in${inView.m4?' vis':''}`}>
              <div className="slabel"> 04 · Dataset & Source</div>
              <div className="stitle">ExPECA Testbed · KTH</div>
              <p className="sbody">Real 5G packet traces from KTH Royal Institute of Technology, Sweden — capturing IP, RLC, MAC and Physical layer behavior.</p>
              <div className="ds-grid">
                <div className="ds-info">
                  {[
                    { label:'Source', val:'ExPECA Testbed, KTH Royal Institute of Technology, Sweden' },
                    { label:'Dataset Size', val:'40,000 records of IP delay measurements in milliseconds' },
                    { label:'Training File', val:'TR1.csv — historical 5G packet delay traces' },
                    { label:'Testing File', val:'TE1.csv — held-out evaluation dataset' },
                    { label:'Key Discovery', val:'Frame Alignment Delay in the RLC layer is the primary cause of the triangular Delay vs. SN pattern' },
                  ].map(i=>(
                    <div className="info-row" key={i.label}>
                      <div className="info-label">{i.label}</div>
                      <div className="info-val">{i.val}</div>
                    </div>
                  ))}
                </div>
                <div>
                  <div className="ds-img" style={{marginBottom:16}}>
                    <img src="/pic2.jpg" alt="5G network city infrastructure"/>
                  </div>
                  <div className="csv-box">
                    <div className="csv-h">CSV Format Required:</div>
                    <div className="csv-r">SN, IP Delay (ms)</div>
                    <div className="csv-r"><span>1</span>, 20.13</div>
                    <div className="csv-r"><span>2</span>, 19.02</div>
                    <div className="csv-r"><span>3</span>, 19.00</div>
                    <div className="csv-r" style={{color:accent,fontSize:11,marginTop:6}}>MIN 30 ROWS REQUIRED</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── TECH STACK ── */}
          

          {/* ── APPLICATIONS ── */}
          <div data-s="m6" ref={ref('m6')}>
            <div className={`fade-in${inView.m6?' vis':''}`}>
              <div className="slabel"> 06 · Real-World Applications</div>
              <div className="stitle">Where This Matters</div>
              <div className="ap-grid">
                {APPS.map(a=>(
                  <div className="ap-card" key={a.title}>
                    <div className="ap-icon">{a.icon}</div>
                    <div className="ap-title">{a.title}</div>
                    <div className="ap-desc">{a.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
